import React, { useState, useEffect } from 'react';
import { AuthenticationAdminResponse, Roles } from '@beable/roster-backend-client-v1';
import { Avatar, Box, Button, Container, CssBaseline, GlobalStyles, TextField, Typography, CircularProgress } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import authService from '../../services/AuthService';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { getStyles } from './styles';

interface JWT {
  username: string;
  email: string;
  customerId: string;
  id?: string;
  role: string;
  iat: number;
  eat: number;
}

function Login() {
  const [params] = useSearchParams();
  const theme = useTheme();
  const styles = getStyles(theme);

  const [inputs, setInputs] = useState({ username: '', password: '', originUrl: window.location.host });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = (event: any) => {
    const submitData = async () => {
      event.preventDefault();
      try {
        setIsLoading(true);
        const adminUser: AuthenticationAdminResponse | null = await authService.login(inputs);
        if (adminUser) {
          setIsLoading(false);
          authService.setUser(adminUser);
          window.location.replace('/');
        } else {
          setIsLoading(false);
          alert("Authentication failed");
        }
      } catch (e) {
        setIsLoading(false);
        alert('Invalid Credentials');
      }
    };
    submitData();
  };

  useEffect(() => {
    const ssoLogin = params.get('ssoSuccess');

    if (ssoLogin === "true") {
      const adminUser: AuthenticationAdminResponse = new AuthenticationAdminResponse();
      const id = params.get('id');
      if (id) adminUser.id = id;

      const username = params.get('username');
      if (username) adminUser.username = username;

      const loginTicketId = params.get('loginTicketId');
      if (loginTicketId) adminUser.loginTicketId = loginTicketId;

      const sessionDuration = params.get('sessionDuration');
      if (sessionDuration) adminUser.sessionDuration = Number(sessionDuration);

      const sessionEnd = params.get('sessionEnd');
      if (sessionEnd) adminUser.sessionEnd = Number(sessionEnd);

      const customerId = params.get('customerId');
      if (customerId) adminUser.customerId = customerId;

      const email = params.get('email');
      if (email) adminUser.email = email;

      const role: string | null = params.get('role');
      if (role && (role in Roles)) adminUser.role = Roles[role as Roles];

      if (adminUser) {
        setIsLoading(false);
        authService.setUser(adminUser);
        window.location.replace('/');
      } else {
        setIsLoading(false);
        alert("Authentication failed");
      }
    } else if (ssoLogin === "false") {
      setIsLoading(false);
      alert("Login with Microsoft failed");
    }

  }, [params])

  const handleInputChange = (event: any) => {
    event.persist();
    setInputs(inputs => ({ ...inputs, [event.target.name]: event.target.value }));
  };

  const usernameInputProps = {
    'data-testid': 'input-username',
  };

  const passwordInputProps = {
    'data-testid': 'input-password',
  };

  return (
    <Container component='main' maxWidth='xs'>
      <CssBaseline />
      <GlobalStyles styles={styles.global} />
      <Box sx={styles.paper}>
        <Avatar sx={styles.avatar}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component='h1' variant='h5'>
          Admin sign in
        </Typography>
        <Box component='form' onSubmit={handleSubmit} sx={styles.form} noValidate>
          <TextField
            variant='outlined'
            margin='normal'
            required
            fullWidth
            data-testid='username'
            id='username'
            label='Username'
            name='username'
            value={inputs.username}
            onChange={handleInputChange}
            autoComplete='username'
            autoFocus
            disabled={isLoading}
            slotProps={{
              htmlInput: usernameInputProps
            }}
          />
          <TextField
            variant='outlined'
            margin='normal'
            required
            fullWidth
            data-testid='password'
            id='password'
            label='Password'
            name='password'
            value={inputs.password}
            type='password'
            onChange={handleInputChange}
            autoComplete='current-password'
            disabled={isLoading}
            slotProps={{
              htmlInput: passwordInputProps
            }}
          />
          {isLoading ? (
            <Box sx={styles.loaderContainer}><CircularProgress disableShrink /></Box>
          ) : (
              <Button
                type='submit'
                fullWidth
                variant='contained'
                color='primary'
                sx={styles.submit}
                data-testid='sign_in'
              >
                Sign In
              </Button>
            )}

        </Box>
      </Box>
    </Container>
  );
}

export default Login;
